import { PlusIcon } from "@radix-ui/react-icons";
import { Link } from "react-router-dom";
import Cookies from "js-cookie";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { useMemo } from "react";

import { Button } from "@/components/ui/button/button";
import { getAllHolidayListQueryOptions } from "@/config/queries/query-options";
import { columns } from "./_components/holiday-list-table/columns";
import { DataTable } from "./_components/holiday-list-table/data-table";
import { QueryError } from "@/components/query-error";

function findOldestAndNewestDates(
  holidays: {
    holidayName: string;
    description: string;
    date: Date;
  }[]
): Date[] {
  if (holidays.length === 0) {
    throw new Error("Array is empty");
  }

  let oldestDate: Date = holidays[0].date;
  let newestDate: Date = holidays[0].date;

  for (const holiday of holidays) {
    if (holiday.date < oldestDate) {
      oldestDate = holiday.date;
    }
    if (holiday.date > newestDate) {
      newestDate = holiday.date;
    }
  }

  return [oldestDate, newestDate];
}

const AdminHolidaysPage = () => {
  const user = Cookies.get("key") as string;

  const { data, isPending, isFetching, isError, error, refetch } = useQuery(
    getAllHolidayListQueryOptions(user)
  );

  const holidaysListData = useMemo(() => {
    if (isPending || isFetching) {
      return [];
    }

    if (data === undefined || data?.foundLists === undefined) {
      return [];
    }

    const result = data?.foundLists?.map((emp) => {
      const [oldDate, newDate] = findOldestAndNewestDates(emp?.holidays);

      return {
        id: emp?._id,
        name: emp?.name,
        fromDate: format(oldDate, "PPP"),
        toDate: format(newDate, "PPP"),
      };
    });

    return result;
  }, [data, isPending, isFetching]);

  return (
    <div className="p-5 h-[calc(100%-56px)]">
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-medium">Holiday List</h3>

        <Button className="flex items-center gap-1" asChild>
          <Link to="/admin/holidays/new">
            <PlusIcon />
            <span>Add Holiday List</span>
          </Link>
        </Button>
      </div>

      {isPending ? (
        <div>Loading</div>
      ) : isError ? (
        <QueryError what="holiday lists" error={error} onRetry={refetch} />
      ) : data?.foundLists?.length ? (
        <div className="mt-5">
          <DataTable data={holidaysListData} columns={columns} />
        </div>
      ) : (
        <div>No Employees</div>
      )}
    </div>
  );
};

export default AdminHolidaysPage;
